import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import CampaignIcon from '@mui/icons-material/Campaign';
import EventIcon from '@mui/icons-material/Event';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import UpdateIcon from '@mui/icons-material/Update';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ContentPreviewModal from '../shared/ContentPreviewModal';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const SectionContainer = styled(Box)({
  padding: '140px 60px',
  background: '#faf9f7',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '500px',
    height: '500px',
    background: 'radial-gradient(circle, rgba(201, 162, 39, 0.08) 0%, transparent 70%)',
    pointerEvents: 'none'
  },
  '@media (max-width: 1024px)': {
    padding: '100px 32px'
  },
  '@media (max-width: 768px)': {
    padding: '80px 20px'
  }
});

const ContentWrapper = styled(Box)({
  maxWidth: '1400px',
  margin: '0 auto',
  position: 'relative',
  zIndex: 10
});

const SectionHeader = styled(Box)({
  textAlign: 'center',
  marginBottom: '72px',
  animation: `${fadeInUp} 0.8s ease-out`
});

const SectionBadge = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  padding: '10px 24px',
  background: 'rgba(201, 162, 39, 0.1)',
  border: '1px solid rgba(201, 162, 39, 0.2)',
  borderRadius: '50px',
  marginBottom: '24px'
});

const BadgeText = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '12px',
  fontWeight: 600,
  color: '#c9a227',
  textTransform: 'uppercase',
  letterSpacing: '2px'
});

const SectionTitle = styled(Typography)({
  fontFamily: "'Playfair Display', serif",
  fontSize: 'clamp(36px, 5vw, 56px)',
  fontWeight: 400,
  color: '#1a1a1a',
  lineHeight: 1.2,
  letterSpacing: '-1px'
});

const GradientText = styled('span')({
  background: 'linear-gradient(135deg, #c9a227 0%, #a67c52 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text'
});

const FeedGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: '28px',
  '@media (max-width: 1024px)': {
    gridTemplateColumns: 'repeat(2, 1fr)'
  },
  '@media (max-width: 600px)': {
    gridTemplateColumns: '1fr'
  }
});

const FeedCard = styled(Box)(({ index }) => ({
  background: '#ffffff',
  borderRadius: '24px',
  overflow: 'hidden',
  cursor: 'pointer',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.04)',
  border: '1px solid rgba(0, 0, 0, 0.06)',
  animation: `${fadeInUp} 0.8s ease-out`,
  animationDelay: `${index * 0.1}s`,
  animationFillMode: 'backwards',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1)',
    '& .card-arrow': {
      opacity: 1,
      transform: 'translateX(0)'
    },
    '& .card-image img': {
      transform: 'scale(1.08)'
    }
  }
}));

const CardImage = styled(Box)({
  width: '100%',
  height: '220px',
  overflow: 'hidden',
  background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
  position: 'relative',
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
  }
});

const PlaceholderImage = styled(Box)(({ type }) => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  background: type === 'event'
    ? 'linear-gradient(135deg, #1a3a2e 0%, #0f2a1e 100%)'
    : type === 'offer'
    ? 'linear-gradient(135deg, #3a2a1a 0%, #2a1a0a 100%)'
    : 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '300px',
    height: '300px',
    background: type === 'event'
      ? 'radial-gradient(circle, rgba(34, 197, 94, 0.15) 0%, transparent 70%)'
      : type === 'offer'
      ? 'radial-gradient(circle, rgba(249, 115, 22, 0.15) 0%, transparent 70%)'
      : 'radial-gradient(circle, rgba(212, 175, 55, 0.15) 0%, transparent 70%)',
    pointerEvents: 'none'
  },
  '& svg': {
    fontSize: '48px',
    color: type === 'event' ? '#22c55e' : type === 'offer' ? '#f97316' : '#d4af37',
    marginBottom: '12px',
    opacity: 0.8
  }
}));

const PlaceholderText = styled(Typography)(({ type }) => ({
  fontFamily: "'Inter', sans-serif",
  fontSize: '12px',
  fontWeight: 600,
  color: type === 'event' ? 'rgba(34, 197, 94, 0.6)' : type === 'offer' ? 'rgba(249, 115, 22, 0.6)' : 'rgba(212, 175, 55, 0.6)',
  textTransform: 'uppercase',
  letterSpacing: '2px'
}));

const CardContent = styled(Box)({
  padding: '28px'
});

const CardHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '16px'
});

const TypeBadge = styled(Box)(({ type }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  padding: '8px 14px',
  fontSize: '11px',
  fontWeight: 600,
  fontFamily: "'Inter', sans-serif",
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  borderRadius: '50px',
  ...(type === 'event' && {
    background: 'rgba(34, 197, 94, 0.1)',
    color: '#22c55e'
  }),
  ...(type === 'offer' && {
    background: 'rgba(249, 115, 22, 0.1)',
    color: '#f97316'
  }),
  ...(!type || type === 'update' || type === 'announcement') && {
    background: 'rgba(201, 162, 39, 0.1)',
    color: '#c9a227'
  },
  '& svg': {
    fontSize: '14px'
  }
}));

const DateText = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '13px',
  fontWeight: 500,
  color: '#888888'
});

const CardTitle = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '20px',
  fontWeight: 600,
  color: '#1a1a1a',
  marginBottom: '12px',
  lineHeight: 1.4
});

const CardDescription = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '15px',
  fontWeight: 400,
  color: '#666666',
  lineHeight: 1.8,
  marginBottom: '24px'
});

const ReadMore = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingTop: '20px',
  borderTop: '1px solid rgba(0, 0, 0, 0.06)'
});

const ReadMoreText = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '14px',
  fontWeight: 600,
  color: '#c9a227'
});

const ArrowIcon = styled(ArrowForwardIcon)({
  fontSize: '18px',
  color: '#c9a227',
  opacity: 0,
  transform: 'translateX(-10px)',
  transition: 'all 0.3s ease'
});

const getTypeIcon = (type) => {
  switch (type) {
    case 'event': return <EventIcon />;
    case 'offer': return <LocalOfferIcon />;
    case 'announcement': return <CampaignIcon />;
    default: return <UpdateIcon />;
  }
};

const getTypeLabel = (type) => {
  switch (type) {
    case 'event': return 'Event';
    case 'offer': return 'Offer';
    case 'announcement': return 'News';
    default: return 'Update';
  }
};

const GMBFeedSection = ({ gmbFeed }) => {
  const [selectedPost, setSelectedPost] = useState(null);
  // Handle both formats: array directly or object with posts array
  const posts = Array.isArray(gmbFeed) ? gmbFeed : (gmbFeed?.posts || []);

  if (!posts || posts.length === 0) return null;

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Get image from various possible field names (matching Template3 logic)
  const getPostImage = (post) => {
    // Primary: Check media array (GMB format)
    if (post.media && post.media.length > 0) {
      return post.media[0]?.googleUrl || post.media[0]?.sourceUrl || post.media[0]?.url || null;
    }
    // Fallback: Check direct image fields
    return post.image || post.imageUrl || post.photo || post.photoUrl ||
           post.thumbnail || post.thumbnailUrl || post.coverImage ||
           post.featuredImage || post.picture || null;
  };

  // Get post type (handle both formats)
  const getPostType = (post) => {
    const type = post.type || post.topicType || 'update';
    // Normalize type names
    if (type === 'EVENT' || type === 'event') return 'event';
    if (type === 'OFFER' || type === 'offer') return 'offer';
    if (type === 'ANNOUNCEMENT' || type === 'announcement') return 'announcement';
    return 'update';
  };

  // Get post title (handle both formats)
  const getPostTitle = (post) => {
    return post.title || post.name || post.summary?.substring(0, 50) || 'Update';
  };

  // Get post summary/content
  const getPostSummary = (post) => {
    const text = post.summary || post.content || post.description || '';
    return text.length > 150 ? text.substring(0, 150) + '...' : text;
  };

  // Get post date
  const getPostDate = (post) => {
    return post.date || post.createTime || post.createdAt || post.publishedAt || null;
  };

  const getPostDescription = (post) => {
    return post.summary || post.content || post.description || '';
  };

  return (
    <SectionContainer id="updates">
      <ContentWrapper>
        <SectionHeader>
          <SectionBadge>
            <BadgeText>Latest Updates</BadgeText>
          </SectionBadge>
          <SectionTitle>
            News & <GradientText>Events</GradientText>
          </SectionTitle>
        </SectionHeader>

        <FeedGrid>
          {posts.slice(0, 3).map((post, index) => {
            const postImage = getPostImage(post);
            const postType = getPostType(post);
            return (
            <FeedCard key={post.id || index} index={index} onClick={() => setSelectedPost(post)}>
              <CardImage className="card-image">
                {postImage ? (
                  <img src={postImage} alt={getPostTitle(post)} loading="lazy" />
                ) : (
                  <PlaceholderImage type={postType}>
                    {getTypeIcon(postType)}
                    <PlaceholderText type={postType}>{getTypeLabel(postType)}</PlaceholderText>
                  </PlaceholderImage>
                )}
              </CardImage>

              <CardContent>
                <CardHeader>
                  <TypeBadge type={postType}>
                    {getTypeIcon(postType)}
                    {getTypeLabel(postType)}
                  </TypeBadge>
                  <DateText>{formatDate(getPostDate(post))}</DateText>
                </CardHeader>

                <CardTitle>{getPostTitle(post)}</CardTitle>
                <CardDescription>
                  {getPostSummary(post)}
                </CardDescription>

                <ReadMore>
                  <ReadMoreText>Read More</ReadMoreText>
                  <ArrowIcon className="card-arrow" />
                </ReadMore>
              </CardContent>
            </FeedCard>
            );
          })}
        </FeedGrid>
      </ContentWrapper>

      <ContentPreviewModal
        open={!!selectedPost}
        onClose={() => setSelectedPost(null)}
        image={selectedPost ? getPostImage(selectedPost) : null}
        imageAlt={selectedPost ? getPostTitle(selectedPost) : 'GMB post'}
        label={selectedPost ? getTypeLabel(getPostType(selectedPost)) : ''}
        subtitle={selectedPost ? formatDate(getPostDate(selectedPost)) : ''}
        title={selectedPost ? getPostTitle(selectedPost) : ''}
        description={selectedPost ? getPostDescription(selectedPost) : ''}
        ctaUrl={selectedPost?.callToAction?.url}
        ctaLabel={selectedPost?.callToAction?.actionType || 'Learn More'}
        palette={{
          accent: '#c9a227',
          tagBg: 'rgba(201, 162, 39, 0.12)',
          tagText: '#c9a227'
        }}
      />
    </SectionContainer>
  );
};

export default GMBFeedSection;
